import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable
} from 'react-beautiful-dnd';
import React, { useEffect, useState } from 'react';
import { cloneDeep, isEmpty } from 'lodash';

import Constants from '../../constants';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Unstable_Grid2';
import { IconButtonWithConfirmation } from '../../../shared/components/IconButtonWithConfirmation';
import LessonResponseService from '../../services/LessonResponseService';
import { SelectionComponent } from '../../models/LessonComponent';
import { SelectionItemEditor } from './SelectionItemEditor';
import { SelectionOption } from '../../models/LessonComponentContent';
import { nanoid } from 'nanoid';
import { useLessonEditContext } from '../../models/LessonEditContext';

interface MultipleSelectionEditorProps {
  component: SelectionComponent;
  hasImage?: boolean;
}

export default function MultipleSelectionEditor(
  props: MultipleSelectionEditorProps
) {
  const options = props.component.content.options || [];
  const isSurvey = props.component?.settings.survey;
  const showBorder = props.component?.settings.selectionSettings?.showBorder;
  const correctResponsePattern = isSurvey
    ? ''
    : props.component.content.correctResponsePattern;
  const [selected, setSelected] = useState<string[]>(getSelectedOptions());

  const { handleSettingsChange } = useLessonEditContext();
  const newItem: SelectionOption = {
    label: '',
    value: nanoid(),
    image: ''
  };

  const getDroppableStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? '#eee' : 'transparent',
    height: '100%'
  });

  useEffect(() => {
    if (correctResponsePattern) {
      setSelected(getSelectedOptions());
    }
  }, [correctResponsePattern]);

  function getSelectedOptions(): string[] {
    if (isSurvey) {
      return [];
    }
    return props.hasImage
      ? (correctResponsePattern && JSON.parse(correctResponsePattern)) || []
      : LessonResponseService.convertMultipleSelectionCorrectResponse(
          correctResponsePattern
        );
  }

  const handleAddOption = (item: SelectionOption) => {
    const copyOptions = cloneDeep(options);
    copyOptions.push(item);
    const copy = cloneDeep(props.component);
    copy.content.options = copyOptions;
    if (copyOptions.length > 0 && !correctResponsePattern) {
      copy.content.correctResponsePattern =
        LessonResponseService.getResponsePattern(props.component.type, [
          copyOptions[0].value
        ]);
    }
    handleSettingsChange(copy);
  };

  const handleOptionChange = (item: SelectionOption) => {
    const copy = cloneDeep(props.component);
    const index = copy.content.options.findIndex(
      (option) => option.value === item.value
    );
    copy.content.options[index] = item;
    handleSettingsChange(copy);
  };

  const handleSelectionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // set the 2nd option as selected if the 1st option is deselected
    // when there is only one option is selected
    let currentSelections: string[] = cloneDeep(selected);
    const defaultSelection = props.component.content.options[0].value;
    if (
      selected.includes(options[0].value) &&
      options[0].value === (event.target as HTMLInputElement).value &&
      options.length > 1 &&
      selected.length === 1
    ) {
      currentSelections = [options[1].value];
    } else {
      currentSelections = !selected?.includes(
        (event.target as HTMLInputElement).value
      )
        ? [...selected, (event.target as HTMLInputElement).value]
        : selected?.filter(
            (e) => e !== (event.target as HTMLInputElement).value
          );
    }

    const copyComp = cloneDeep(props.component);
    copyComp.content.correctResponsePattern =
      LessonResponseService.getResponsePattern(
        props.component.type,
        !isEmpty(currentSelections) ? currentSelections : [defaultSelection]
      );
    handleSettingsChange(copyComp);
  };

  const handleDeleteOption = (id: string) => {
    const copyOptions = cloneDeep(options);
    const optionIndex = copyOptions.findIndex((o) => o.value === id);
    copyOptions.splice(optionIndex, 1);
    const copyComp = cloneDeep(props.component);
    copyComp.content.options = copyOptions;
    /** If the deleted option is in the correctResponsePattern list, that should be removed */
    const currentOptionsSelected = selected?.filter((e) => e !== id);
    if (!isEmpty(currentOptionsSelected)) {
      copyComp.content.correctResponsePattern =
        LessonResponseService.getResponsePattern(
          props.component.type,
          currentOptionsSelected
        );
    } else {
      copyComp.content.correctResponsePattern =
        LessonResponseService.getResponsePattern(
          props.component.type,
          copyOptions.length > 0 ? [copyOptions[0].value] : null
        );
    }
    handleSettingsChange(copyComp);
  };

  const handleDragEnd = (result: DropResult) => {
    // prevent from destination index outbound error
    if (!result.destination) return;
    const fromIndex = result.source.index;
    const toIndex = result.destination.index;
    const copy = cloneDeep(props.component);
    const movingItem = copy.content.options[fromIndex];
    copy.content.options.splice(fromIndex, 1);
    copy.content.options.splice(toIndex, 0, movingItem);
    handleSettingsChange(copy);
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="selection-options">
          {(provided1, snapshot1) => (
            <div
              ref={provided1.innerRef}
              style={getDroppableStyle(snapshot1.isDraggingOver)}
            >
              <FormGroup>
                {options?.map((option, index) => (
                  <Draggable
                    key={option.value}
                    draggableId={option.value}
                    index={index}
                  >
                    {(provided2, snapshot2) => (
                      <div
                        ref={provided2.innerRef}
                        {...provided2.draggableProps}
                      >
                        <Grid container alignItems="center">
                          <SelectionItemEditor
                            componentKey={props.component.componentkey}
                            hasImage={props.hasImage}
                            isMultiple
                            checked={
                              isSurvey
                                ? false
                                : selected?.includes(option.value)
                            }
                            selectionCardItem={option}
                            onItemChange={handleOptionChange}
                            onSelectionChange={
                              isSurvey ? undefined : handleSelectionChange
                            }
                            correctResponse={selected}
                            showBorder={showBorder}
                          />
                          <Grid
                            {...provided2.dragHandleProps}
                            style={{ paddingTop: '3px' }}
                          >
                            <span
                              style={{
                                color: 'rgba(0,0,0,0.54)' //* to make it look the same as the 'delete' icon button
                              }}
                            >
                              <DragIndicatorIcon />
                            </span>
                          </Grid>
                          <Grid>
                            <IconButtonWithConfirmation
                              size="small"
                              onConfirm={() => handleDeleteOption(option.value)}
                            >
                              <DeleteIcon />
                            </IconButtonWithConfirmation>
                          </Grid>
                        </Grid>
                      </div>
                    )}
                  </Draggable>
                ))}
              </FormGroup>
              {provided1.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {/* Hide 'Click to add option' for selection card components when max number of options reach */}
      {(!props.hasImage ||
        (options.length < Constants.SelectionCardMaxNumItems &&
          props.hasImage)) && (
        <Grid container direction="row" alignItems="center">
          <SelectionItemEditor
            componentKey={props.component.componentkey}
            hasImage={props.hasImage}
            isMultiple
            selectionCardItem={newItem}
            onItemChange={handleAddOption}
            onSelectionChange={handleSelectionChange}
            showBorder={showBorder}
          />
        </Grid>
      )}
    </>
  );
}
